import { useEffect, useRef, type ReactNode } from "react";

import { useDismissOnEscape } from "../../hooks/useDismissOnEscape";
import { SHOP_FREE_ROLES, type UserListEntry } from "../../api/types";
import { APP_LANGUAGE_OPTIONS } from "../../i18n/languages";
import { money, percentText } from "../../i18n/intl";
import type { TeamMemberPermissions } from "../../utils/teamMemberPermissions";
import { TeamActivityList } from "./TeamActivityList";
import {
  accountBadge,
  courierLine,
  distinctJobTitle,
  initials,
  payDayLabel,
  roleLabel,
  shortDate,
  unconfirmedEmail,
} from "./teamLabels";
import { TeamEmail } from "./TeamEmail";

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="team-panel-section" aria-label={title}>
      <div className="team-panel-section-head">
        <h3>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="team-panel-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * G-145 — one person, everything this page can do for them, grouped the way
 * the owner signed off (mockup 2026-10-13): Contact, Shop, Pay, Contract,
 * Sign-in & shop floor, Recent activity, Deactivate.
 *
 * ONE component, two shapes, chosen by CSS alone (`.team-sheet`): a bottom
 * sheet on a phone (the stock list's `.inv-sheet` shell, as G-102 built it)
 * and a full-height panel docked on the right at 1001 px and wider. Every
 * action still opens its own modal ON TOP (G-106): the panel stays, the modal
 * returns you to the same person.
 *
 * Every control is gated by `teamMemberPermissions` — the one definition of
 * who may do what — never by a check of its own.
 */
export function TeamMemberSheet({
  user,
  permissions,
  onEditContact,
  onEditContract,
  onViewContract,
  onResetPassword,
  onManageInvite,
  onEditPay,
  onSetPin,
  onReassign,
  onDeactivate,
  onSetLanguage,
  onClose,
  behindModal,
}: {
  user: UserListEntry;
  permissions: TeamMemberPermissions;
  onEditContact: () => void;
  onEditContract: () => void;
  onViewContract: () => void;
  onResetPassword: () => void;
  onManageInvite: () => void;
  onEditPay: () => void;
  onSetPin: () => void;
  onReassign: () => void;
  onDeactivate: () => void;
  onSetLanguage: (value: string) => void;
  onClose: () => void;
  /** True while one of the modals this panel opens is covering it. */
  behindModal: boolean;
}) {
  /*
   * G-106 — the panel STAYS OPEN under the modal each action opens, so Escape
   * has to close one layer at a time. `useDismissOnEscape` keeps the order.
   */
  useDismissOnEscape(onClose);

  /*
   * ...and while a modal covers it, the panel is INERT. `Modal` has no focus
   * trap, so without this a Tab out of the modal walks into the rows behind
   * it — including Deactivate, obscured but still reachable with Enter — and
   * the modal's `aria-modal="true"` tells a screen reader the opposite.
   * Set as an attribute because React 18 does not type `inert`.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    rootRef.current?.toggleAttribute("inert", behindModal);
  }, [behindModal]);

  const current = user.locationAssignments[0];
  const badge = accountBadge(user);
  const vehicle = courierLine(user);
  // G-102's "double text" ruling: no job title that only repeats the role badge above.
  const jobTitle = vehicle ? null : distinctJobTitle(user);
  const showSignIn =
    permissions.canManageInvite ||
    permissions.canResetPassword ||
    permissions.canSetPin ||
    permissions.canSetLanguage;

  return (
    <div
      ref={rootRef}
      className="inv-sheet-backdrop team-sheet-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="inv-sheet team-sheet" role="dialog" aria-modal="true" aria-label={user.fullName}>
        <div className="team-panel-head">
          <span className="team-tile-avatar team-panel-avatar" aria-hidden="true">
            {initials(user)}
          </span>
          <div className="team-panel-who">
            <h2 className="team-panel-name">{user.fullName}</h2>
            <div className="team-tile-badges">
              <span className="badge primary">{roleLabel(user.role)}</span>
              {!user.isActive ? <span className="badge muted">inactive</span> : null}
              {badge ? <span className={badge.className}>{badge.label}</span> : null}
              {unconfirmedEmail(user) ? (
                <span className="badge warning">Email not confirmed</span>
              ) : null}
            </div>
          </div>
          <button type="button" className="icon-button team-panel-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <Section
          title="Contact"
          action={
            permissions.canEditContact ? (
              <button type="button" className="button plain" onClick={onEditContact}>
                Edit
              </button>
            ) : null
          }
        >
          <dl className="team-panel-rows">
            <Row label="Phone">{user.phoneNumber || "—"}</Row>
            <Row label="Email">
              <span className="team-panel-email">
                <TeamEmail user={user} />
              </span>
            </Row>
            {jobTitle ? <Row label="Job title">{jobTitle}</Row> : null}
          </dl>
        </Section>

        <Section
          title={vehicle ? "Vehicle" : "Shop"}
          action={
            permissions.canReassign ? (
              <button type="button" className="button plain" onClick={onReassign}>
                Reassign
              </button>
            ) : null
          }
        >
          <dl className="team-panel-rows">
            {vehicle ? (
              <Row label="Vehicle">{vehicle}</Row>
            ) : (
              <>
                <Row label="Works at">
                  {current ? (
                    <>
                      {current.location.name}{" "}
                      <span className="muted">({current.location.code})</span>
                    </>
                  ) : SHOP_FREE_ROLES.has(user.role) || user.role === "OWNER" || user.role === "ADMIN" ? (
                    <span className="muted">All shops</span>
                  ) : (
                    <span className="muted">No shop</span>
                  )}
                </Row>
                {current ? <Row label="Since">{shortDate(current.startedAt)}</Row> : null}
              </>
            )}
          </dl>
        </Section>

        {permissions.canEditPay ? (
          <Section
            title="Pay"
            action={
              <button type="button" className="button plain" onClick={onEditPay}>
                Edit
              </button>
            }
          >
            <dl className="team-panel-rows">
              {user.monthlySalaryEur ? (
                <Row label="Monthly salary">{money(user.monthlySalaryEur)}</Row>
              ) : (
                <Row label="Hourly rate">
                  {money(user.hourlyRateEur)}
                </Row>
              )}
              {user.upcomingSalary ? (
                <Row label={`From ${user.upcomingSalary.fromMonth}`}>
                  {user.upcomingSalary.monthlySalaryEur != null ? money(user.upcomingSalary.monthlySalaryEur) : "Hourly"}
                </Row>
              ) : null}
              <Row label="Commission">
                {user.salesCommissionPercent ? `${percentText(user.salesCommissionPercent)} %` : "—"}
              </Row>
              <Row label="Pay day">{payDayLabel(user.payDayOfMonth ?? 1)}</Row>
            </dl>
          </Section>
        ) : null}

        {permissions.canUploadContract ? (
          <Section
            title="Contract"
            action={
              <button type="button" className="button plain" onClick={onEditContract}>
                {user.workContractConfigured ? "Replace PDF" : "Upload PDF"}
              </button>
            }
          >
            {user.workContractConfigured ? (
              <div className="team-panel-file">
                <span className="team-panel-file-name">
                  {user.workContractOriginalName || "Contract PDF"}
                </span>
                <button type="button" className="button plain" onClick={onViewContract}>
                  View
                </button>
              </div>
            ) : (
              <p className="muted team-panel-empty">Not uploaded</p>
            )}
          </Section>
        ) : null}

        {showSignIn ? (
          <Section title="Sign-in & shop floor">
            {permissions.canManageInvite ? (
              <div className="team-panel-action">
                <span>Invitation</span>
                <button type="button" className="button tinted" onClick={onManageInvite}>
                  Manage invitation
                </button>
              </div>
            ) : null}
            {permissions.canResetPassword ? (
              <div className="team-panel-action">
                <span>Password</span>
                <button type="button" className="button tinted" onClick={onResetPassword}>
                  Send reset link
                </button>
              </div>
            ) : null}
            {permissions.canSetPin ? (
              <div className="team-panel-action">
                <span className="team-panel-action-label">
                  Shop-floor PIN
                  <span className={user.shopPinConfigured ? "badge success" : "badge muted"}>
                    {user.shopPinConfigured ? "Set" : "Not set"}
                  </span>
                </span>
                <button type="button" className="button tinted" onClick={onSetPin}>
                  PIN &amp; fob
                </button>
              </div>
            ) : null}
            {permissions.canSetLanguage ? (
              <div className="team-panel-action">
                <label htmlFor={`panel-language-${user.id}`}>Language</label>
                <select
                  id={`panel-language-${user.id}`}
                  className="team-panel-select"
                  value={user.language ?? ""}
                  onChange={(e) => onSetLanguage(e.target.value)}
                >
                  <option value="">Default — English</option>
                  {APP_LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value} lang={option.lang}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </Section>
        ) : null}

        {/* G-105 — what they have done, in place, with its own links into the Action log. */}
        {permissions.canViewActivity ? (
          <div className="team-panel-section">
            <TeamActivityList user={user} take={5} />
          </div>
        ) : null}

        {permissions.canDeactivate ? (
          <div className="team-panel-section team-panel-danger">
            {/* The endpoint deactivates (DELETE /users/:id); the old "Delete"
                label said otherwise. There is no way back yet — KI-16. */}
            <button type="button" className="button danger" onClick={onDeactivate}>
              Deactivate
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
